import React, { useMemo } from 'react';
import { Box, HStack, Text, IconButton, useColorModeValue } from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { FiEye, FiX } from 'react-icons/fi';
import { useImpersonation } from '../../contexts/ImpersonationContext';

/**
 * Reusable admin impersonation picker + banner.
 *
 * Drop into any page to let admins view it as another user.
 * The consuming page provides the user list — this keeps the component
 * agnostic about where users come from (CRM users, all users, EOS users, etc.).
 *
 * @param {Array}  users   - [{ uid, name, email, ... }]
 * @param {boolean} loading - whether the user list is still loading
 * @param {string} [label]  - picker label (default "View as user")
 */
export default function ImpersonationBar({ users = [], loading = false, label = 'View as user' }) {
  const {
    impersonatedUser,
    isImpersonating,
    canImpersonate,
    setImpersonatedUser,
    clearImpersonation,
  } = useImpersonation();

  const bg = useColorModeValue('blue.50', 'blue.900');
  const inactiveBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('blue.200', 'blue.700');
  const inactiveBorder = useColorModeValue('gray.200', 'gray.700');

  const options = useMemo(
    () =>
      users.map((u) => ({
        value: u.uid,
        label: u.name || u.email || u.uid,
        email: u.email,
        _user: u,
      })),
    [users]
  );

  const selectedOption = useMemo(() => {
    if (!isImpersonating) return null;
    return (
      options.find((o) => o.value === impersonatedUser?.uid) || {
        value: impersonatedUser?.uid,
        label: impersonatedUser?.name || impersonatedUser?.uid,
      }
    );
  }, [isImpersonating, impersonatedUser, options]);

  if (!canImpersonate) return null;

  const handleChange = (option) => {
    if (!option) {
      clearImpersonation();
      return;
    }
    setImpersonatedUser(option._user || { uid: option.value, name: option.label, email: '' });
  };

  const formatOptionLabel = (opt) => (
    <HStack spacing={2}>
      <Text fontSize="sm" fontWeight="medium">{opt.label}</Text>
      {opt.email && (
        <Text fontSize="xs" color="gray.500">{opt.email}</Text>
      )}
    </HStack>
  );

  return (
    <Box
      bg={isImpersonating ? bg : inactiveBg}
      borderWidth="1px"
      borderColor={isImpersonating ? borderColor : inactiveBorder}
      borderRadius="md"
      px={3}
      py={2}
      mb={4}
      transition="all 0.15s"
    >
      <HStack spacing={3}>
        <HStack spacing={1.5} flexShrink={0}>
          <FiEye size={14} />
          <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
            {label}
          </Text>
        </HStack>

        <Box flex={1} minW="200px" maxW="360px">
          <Select
            size="sm"
            placeholder="Select a user..."
            value={selectedOption}
            onChange={handleChange}
            options={options}
            isClearable
            isLoading={loading}
            formatOptionLabel={formatOptionLabel}
            chakraStyles={{
              container: (base) => ({ ...base, fontSize: 'sm' }),
              dropdownIndicator: (base) => ({ ...base, paddingX: 1 }),
            }}
            noOptionsMessage={() => 'No users found'}
          />
        </Box>

        {isImpersonating && (
          <IconButton
            icon={<FiX />}
            size="xs"
            variant="ghost"
            aria-label="Stop impersonating"
            onClick={clearImpersonation}
          />
        )}
      </HStack>
    </Box>
  );
}
